import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CircleHelp, Sparkles } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import { showsReleaseNotes } from "@/shared/brand/bootstrap";
import { useBrand } from "@/shared/brand/store";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { releaseNotes } from "@/content/releaseNotes";
import { shouldShowWhatsNew } from "@/content/releaseVersion";

/** localStorage 键:上次看过的版本号。 */
export const LAST_SEEN_RELEASE_KEY = "gscq.lastSeenRelease";

/**
 * localStorage 三件套全部 try/catch —— 隐私窗口、站点数据被清、企业策略禁用本地存储时
 * `localStorage` 的 getter 本身就会抛。抛在这里的后果是**整个壳白屏**(它挂在 AdminLayout 上),
 * 为了一个「要不要弹更新提示」把人挡在门外,不划算。读不到 = 当没看过;写不进 = 下次再弹一遍。
 */
function readLastSeen(): string | null | undefined {
  try {
    return localStorage.getItem(LAST_SEEN_RELEASE_KEY);
  } catch {
    return undefined; // 读不到:既不是「没看过」也不是「看过了」—— 一律不弹,别在坏环境里骚扰人
  }
}

function writeLastSeen(version: string): void {
  try {
    localStorage.setItem(LAST_SEEN_RELEASE_KEY, version);
  } catch {
    /* 写不进就算了:最坏是下次登录再弹一次,不影响任何功能 */
  }
}

/**
 * 1004 F1b:本会话已弹过的标记(sessionStorage)。复测里渠道新租户首登后每切一个页面(整页刷新)都弹一次「新手指引」——
 * 没点关闭就不写 lastSeen,于是每次挂载都当「第一次来」。一个会话(标签页)只弹一次,关掉照旧写 lastSeen 永久不弹。
 */
export const SHOWN_THIS_SESSION_KEY = "gscq.whatsNewShownThisSession";

function shownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SHOWN_THIS_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markShownThisSession(): void {
  try {
    sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, "1");
  } catch {
    /* 写不进:最坏每次刷新再弹,与改造前相同 */
  }
}

type Mode = "welcome" | "whatsNew";

/**
 * 首次打开弹一次(契约 §21.3)。
 *
 * ★两种形态,由 **key 在不在**分:
 *   - key 不存在 = 这个浏览器第一次登录 → 弹「新手指引」入口。第一次进来的人不关心「本次更新」,
 *     他连上一版是什么都不知道;把更新清单糊他一脸,是拿我们的叙事挡他的第一步。
 *   - key 存在、且与最新版本跨 MINOR(`shouldShowWhatsNew`,0925 拍板)→ 弹「本次更新」。
 *     同 MINOR 的 PATCH 升级(缺陷/欠账/小功能,天天可能升号)**不弹**——弹了等于没弹,
 *     用户会学会无视这个弹框。PATCH 的条目仍能在 `/help/changelog` 里看到。
 *   - key 等于最新版本,或与最新版本同 MINOR → 不弹。**关闭即写 key**,写完整版本号
 *     (不是只写 MINOR 那两段)—— 否则下次比较就退化成永远只跟 MINOR 比,精度丢了。
 * ★平台超管不弹:更新清单讲的是租户舱里的事,平台侧看它没有意义。
 * ★1004 O8:代客态一律不弹(平台 / 渠道运营在替客户看,不是客户第一次来;此前每次代客都弹新手指引)。
 *   也不写 key —— 那是客户自己浏览器的「看过没」,代客不该替他标记。
 * ★1004 O5:渠道 / 中性品牌不弹「本次更新」(我方版本号与发版说明);「新手指引」照弹。
 * ★只在挂载时判一次(`useEffect` 依赖只有登录态与 scope):弹窗不该在用户用着用着突然冒出来。
 */
export function WhatsNewDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, effectiveScope } = useAuth();
  const { isImpersonating } = useImpersonationOptional();
  const releaseNotesVisible = showsReleaseNotes(useBrand());
  const [mode, setMode] = useState<Mode | null>(null);

  const latest = releaseNotes[0];

  useEffect(() => {
    if (!isAuthenticated) return;
    // scope 还没定(权限未到)先不弹:此刻判不出他是不是平台超管。
    if (!effectiveScope || effectiveScope !== "TENANT") return;
    if (isImpersonating) return;
    if (!latest?.version) return;
    if (shownThisSession()) return;

    const seen = readLastSeen();
    if (seen === undefined) return; // localStorage 不可用
    if (seen === null) {
      markShownThisSession();
      setMode("welcome");
      return;
    }
    if (seen === latest.version) return;
    // 0925 拍板:同 MINOR 的 PATCH 升级不弹,PATCH 条目只在 /help/changelog 里看得到。
    if (!shouldShowWhatsNew(seen, latest.version)) return;
    if (!releaseNotesVisible) return;
    markShownThisSession();
    setMode("whatsNew");
  }, [isAuthenticated, effectiveScope, isImpersonating, releaseNotesVisible, latest?.version]);

  if (!mode || !latest) return null;

  const close = () => {
    writeLastSeen(latest.version);
    setMode(null);
  };

  const goHelp = (to: string) => {
    close();
    navigate(to);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="max-w-lg" data-whatsnew={mode}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "welcome" ? (
              <CircleHelp className="h-5 w-5 text-primary" />
            ) : (
              <Sparkles className="h-5 w-5 text-primary" />
            )}
            {mode === "welcome" ? t("help.welcomeTitle") : t("help.whatsNewTitle")}
          </DialogTitle>
          <DialogDescription>
            {mode === "welcome"
              ? t("help.welcomeBody")
              : t("help.whatsNewVersion", { version: latest.version, date: latest.date })}
          </DialogDescription>
        </DialogHeader>

        {mode === "whatsNew" && (
          // 弹窗里只放清单,长了自己滚 —— 不做分页、不做折叠:这是一屏扫完的东西。
          // ★上下两道 hairline:没有它,滚动区被裁掉的那半行看起来像渲染坏了而不是「下面还有」。
          <ul className="max-h-[46vh] space-y-2 overflow-y-auto border-y py-2 pr-1">
            {latest.items.map((item) => (
              <li
                key={`${item.cabin}-${item.text}`}
                className="flex gap-2 text-[13px] leading-relaxed"
              >
                {/* ★`self-start` 不能省:flex 默认 stretch,两行的条目会把这枚舱名标签拉成一个
                    高 44px 的椭圆(0919 真渲染截图抓到)。`whitespace-nowrap` 防「行业知识库」折行。 */}
                <span className="mt-0.5 shrink-0 self-start whitespace-nowrap rounded-full border px-1.5 py-0.5 text-2xs text-muted-foreground">
                  {item.cabin}
                </span>
                <span className="min-w-0">{item.text}</span>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {mode === "welcome" ? t("help.welcomeClose") : t("help.whatsNewClose")}
          </Button>
          <Button onClick={() => goHelp(mode === "welcome" ? "/help" : "/help/changelog")}>
            {mode === "welcome" ? t("help.welcomeCta") : t("help.whatsNewAll")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
